import { useEffect, useState } from "react";
import {
  Select,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  FormHelperText,
  SelectChangeEvent,
  SelectProps,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../reduxStore/Store";
import { User } from "../../models/models";
import { fetchUsersByOwner, setSelectedUser } from "../../reduxStore/UserSlice";

interface SelectUserComponentProps
  extends Omit<SelectProps<number | string>, "value" | "onChange"> {
  onUserChange: (selected: User | null) => void;
  selectedUser?: User | null;
}

const SelectUser: React.FC<SelectUserComponentProps> = ({
  onUserChange,
  selectedUser,
  required = false,
  ...selectProps
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { userData, loading, error } = useSelector(
    (state: RootState) => state.UserSlice
  );
  const [selectedId, setSelectedId] = useState<number | string>("");

  useEffect(() => {
    dispatch(fetchUsersByOwner({ limit: 100, offset: 0 }));
  }, [dispatch]);

  useEffect(() => {
    if (required && !selectedUser && userData.length > 0 && selectedId === "") {
      const firstUser = userData[0];
      setSelectedId(firstUser.id);
      onUserChange(firstUser);
      dispatch(setSelectedUser(firstUser));
    }
  }, [required, selectedUser, userData, selectedId, dispatch, onUserChange]);

  useEffect(() => {
    if (selectedUser) {
      setSelectedId(selectedUser.id);
    }
  }, [selectedUser]);

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    const id = event.target.value;
    setSelectedId(id);
    const selected =
      id === "" ? null : userData.find((u) => u.id === Number(id)) || null;
    onUserChange(selected);
    dispatch(setSelectedUser(selected));
  };

  // 🌀 Avoid rendering dropdown while loading and data is still empty
  if (loading && userData.length === 0) {
    return <CircularProgress />;
  }

  return (
    <FormControl fullWidth variant="outlined" error={!!error} required={required}>
      <InputLabel id="select-user-label">Utilisateur</InputLabel>
      <Select
        labelId="select-user-label"
        value={selectedId}
        onChange={handleChange}
        label="Utilisateur"
        disabled={loading || userData.length === 0}
        size="small"
        {...selectProps}
      >
        {!required && <MenuItem value=""><em>Aucun</em></MenuItem>}
        {userData.map((user) => (
          <MenuItem key={user.id} value={user.id}>
            {user.login}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default SelectUser;
